/** Saved views as tabs above the filter line: "All", the shipped starters, then the person's own.
 *
 * A view is filters + sort + visible columns + grouping (`views.ts`). A dot on the active tab means
 * the table has moved off it; **Save** writes the change into the view (a locked starter cannot be
 * written, so it offers only **Save as new**). The menu beside the tabs renames, duplicates, deletes
 * and sets "my default". The starters are locked: never edited, renamed or deleted, only duplicated.
 *
 * They are drawn as tabs but are toggle buttons (`aria-pressed`), not an ARIA tablist: a tab must control
 * a panel of its own, and a view is a lens over the one table below it.
 *
 * Views are stored per person in this browser; the info control says an account-level store is not
 * built yet (it needs an endpoint that does not exist).
 */

import { DotsThree, Star } from "@phosphor-icons/react";
import { useState } from "react";
import type { FormEvent } from "react";

import { InfoPopover } from "../InfoPopover";
import { PopMenu } from "./PopMenu";
import styles from "./tablePower.module.css";
import type { GridPower } from "./useGridPower";

type Naming = { mode: "new" } | { mode: "rename"; id: string } | null;

export function ViewTabs({ power }: { power: GridPower }) {
  const { views } = power;
  const [naming, setNaming] = useState<Naming>(null);
  const [draft, setDraft] = useState("");
  const active = views.list.find((v) => v.id === views.activeId);
  const locked = active?.locked === true;

  const begin = (next: Naming, initial: string) => {
    setDraft(initial);
    setNaming(next);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (naming?.mode === "new") views.saveAsNew(draft);
    else if (naming?.mode === "rename") views.rename(naming.id, draft);
    setNaming(null);
  };
  return (
    <div className={styles.views} data-testid="view-tabs">
      <div role="group" aria-label="Saved views" className={styles.tabs}>
        {views.list.map((view) => {
          const on = view.id === views.activeId;
          return (
            <button
              key={view.id}
              type="button"
              aria-pressed={on}
              data-view={view.id}
              data-locked={view.locked || undefined}
              className={`${styles.tab} ${on ? styles.tabOn : ""}`}
              onClick={() => views.select(view.id)}
            >
              {view.name}
              {views.defaultId === view.id ? (
                <Star size={11} weight="fill" aria-label="My default" className={styles.star} />
              ) : null}
              {on && views.dirty ? (
                <>
                  <span className={styles.dot} aria-hidden="true" data-testid="view-dirty" />
                  <span className="visuallyHidden"> (unsaved changes)</span>
                </>
              ) : null}
            </button>
          );
        })}
      </div>
      {naming ? (
        <form className={styles.nameForm} onSubmit={submit}>
          <input
            autoFocus
            value={draft}
            aria-label="View name"
            maxLength={40}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                setNaming(null);
              }
            }}
          />
          <button type="submit" className={styles.primary} disabled={draft.trim() === ""}>
            Save
          </button>
          <button type="button" className={styles.tool} onClick={() => setNaming(null)}>
            Cancel
          </button>
        </form>
      ) : (
        <div className={styles.viewActions}>
          {views.canSave ? (
            <button type="button" className={styles.primary} data-testid="view-save" onClick={views.save}>
              Save
            </button>
          ) : null}
          {views.dirty ? (
            <button
              type="button"
              className={styles.tool}
              data-testid="view-save-new"
              onClick={() => begin({ mode: "new" }, "")}
            >
              Save as new
            </button>
          ) : null}
          <PopMenu
            label="View options"
            align="right"
            testId="view-menu"
            trigger={<DotsThree size={16} weight="bold" aria-hidden="true" />}
            triggerClassName={styles.iconTool}
          >
            {(close) => (
              <>
                {!locked ? (
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.item}
                    onClick={() => {
                      close();
                      begin({ mode: "rename", id: views.activeId }, active?.name ?? "");
                    }}
                  >
                    Rename
                  </button>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  className={styles.item}
                  onClick={() => {
                    views.duplicate(views.activeId);
                    close();
                  }}
                >
                  Duplicate
                </button>
                {!locked ? (
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.item}
                    onClick={() => {
                      views.remove(views.activeId);
                      close();
                    }}
                  >
                    Delete
                  </button>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  className={styles.item}
                  onClick={() => {
                    views.setDefault(views.defaultId === views.activeId ? null : views.activeId);
                    close();
                  }}
                >
                  {views.defaultId === views.activeId ? "Remove as my default" : "Set as my default"}
                </button>
              </>
            )}
          </PopMenu>
          <InfoPopover infoKey="shell.table-views" />
        </div>
      )}
    </div>
  );
}
